import { useCallback, useId, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import type { Attendee, EventDetail, RsvpAnswer } from '@grc/shared';
import { ApiError } from '../../api';
import { ErrorLine, NoteLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { LOCALE, t, type Code } from '../../i18n';
import { clubDate, dayHeading, endsAt, timeRange } from './dates';
import { typeText, type FromState } from './EventList';
import { answerEvent, eventPage } from './events-api';
import { Unloaded, useLoad } from './Load';

/**
 * An event's page (B3; D33, D34), at /events/:id: when, where and the notes; Yes, Maybe and No,
 * the member's own answer lit, taken until the event starts and never for a cancelled one; and
 * who's coming, who said yes and who said maybe, by name (never the no's). A cancelled event says
 * so under its title, its buttons off and who's coming hidden (the owner's answer, 4A).
 */
export function EventPage() {
  const { id = '' } = useParams();
  // Each event's page starts afresh, from loading.
  return <EventById key={id} id={id} />;
}

/** Where All events goes: the view the event was opened from, as its row says, or Upcoming. */
function backPath(state: unknown): string {
  const from = (state as Partial<FromState> | null)?.from;
  return typeof from === 'string' && /^\/events(\/|\?|$)/.test(from) ? from : '/events';
}

function isGone(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'not_found';
}

function EventById({ id }: { id: string }) {
  const location = useLocation();
  const load = useCallback(() => eventPage(id), [id]);
  const { state, retry } = useLoad(load);
  // Removed while the page was open (an admin's "cancel this and the following", D33).
  const [removed, setRemoved] = useState(false);
  const gone = removed || (state.status === 'failed' && isGone(state.error));

  return (
    <section className="screen screen-narrow">
      <Link to={backPath(location.state)} className="back-link">
        {t('event.back')}
      </Link>
      {gone ? (
        <p>{t('event.gone')}</p>
      ) : state.status === 'loaded' ? (
        <EventDetails event={state.value} onGone={() => setRemoved(true)} />
      ) : (
        <Unloaded state={state} onRetry={retry} />
      )}
    </section>
  );
}

const ANSWERS: readonly { answer: RsvpAnswer; label: Code }[] = [
  { answer: 'yes', label: 'event.yes' },
  { answer: 'maybe', label: 'event.maybe' },
  { answer: 'no', label: 'event.no' },
];

/** The map link, if it's https: the API keeps no other (D33), and the page links no other. */
function mapLink(url: string | null): string | undefined {
  if (url === null) return undefined;
  try {
    return new URL(url).protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

function EventDetails({ event: first, onGone }: { event: EventDetail; onGone: () => void }) {
  // The page's moment, for Today and for whether the event has started: as it opened. Should it
  // start while the page is open, the server refuses the answer, and the page says so.
  const [now] = useState(() => Date.now());
  const [event, setEvent] = useState(first);
  const [busy, setBusy] = useState<RsvpAnswer>();
  const [error, setError] = useState<string>();
  // The server said it has started: the phone's clock may be behind the server's.
  const [startSeen, setStartSeen] = useState(false);
  const questionId = useId();
  const whoId = useId();

  const cancelled = event.status === 'cancelled';
  const started = startSeen || Date.parse(event.startsAt) <= now;
  const closed = cancelled || started;
  const map = mapLink(event.mapUrl);

  async function onAnswer(answer: RsvpAnswer) {
    // The answer it has already: nothing to send.
    if (busy !== undefined || closed || answer === event.myAnswer) return;
    setBusy(answer);
    setError(undefined);
    try {
      setEvent(await answerEvent(event.id, answer));
    } catch (err) {
      if (isGone(err)) {
        onGone();
        return;
      }
      // The event changed meanwhile: the page shows it as it is now, and the line says why the
      // answer wasn't taken.
      if (err instanceof ApiError && err.code === 'event_started') setStartSeen(true);
      if (err instanceof ApiError && err.code === 'event_cancelled') {
        setEvent((shown) => ({ ...shown, status: 'cancelled' }));
      }
      setError(errorText(err));
    }
    setBusy(undefined);
  }

  return (
    <>
      <h1>{event.title}</h1>
      <p className="muted event-type">{typeText(event.type)}</p>
      <NoteLine text={cancelled ? t('event.cancelledNote') : undefined} />
      <dl className="event-details">
        <div>
          <dt>{t('event.when')}</dt>
          <dd>
            <span>{dayHeading(clubDate(event.startsAt), clubDate(now))}</span>
            <span>{timeRange(event)}</span>
          </dd>
        </div>
        {(event.place !== null || map !== undefined) && (
          <div>
            <dt>{t('event.where')}</dt>
            <dd>
              {event.place !== null && <span>{event.place}</span>}
              {map !== undefined && (
                <a className="text-link" href={map} target="_blank" rel="noopener noreferrer">
                  {t('event.map')}
                </a>
              )}
            </dd>
          </div>
        )}
        {event.description !== null && (
          <div>
            <dt>{t('event.notes')}</dt>
            <dd className="event-notes">{event.description}</dd>
          </div>
        )}
      </dl>
      {/* The question names the buttons' group, not a region of its own: said once. */}
      <section className="rsvp">
        <h2 id={questionId}>{t('event.question')}</h2>
        <div className="rsvp-buttons" role="group" aria-labelledby={questionId}>
          {ANSWERS.map(({ answer, label }) => (
            <button
              key={answer}
              type="button"
              className="rsvp-button"
              aria-pressed={event.myAnswer === answer}
              disabled={closed || busy !== undefined}
              onClick={() => void onAnswer(answer)}
            >
              {busy === answer ? t('event.saving') : t(label)}
            </button>
          ))}
        </div>
        <ErrorLine text={error} />
        {/* Why the buttons are off once it has started; a cancellation is said under the title. */}
        {error === undefined && !cancelled && started && (
          <p className="muted rsvp-closed">
            {endsAt(event) <= now ? t('event.over') : t('error.eventStarted')}
          </p>
        )}
      </section>
      {!cancelled && (
        <section className="attendees" aria-labelledby={whoId}>
          <h2 id={whoId}>{t('event.who')}</h2>
          <Attendees
            heading={t('event.yesCount', { count: event.answers.yes.length })}
            people={event.answers.yes}
          />
          <Attendees
            heading={t('event.maybeCount', { count: event.answers.maybe.length })}
            people={event.answers.maybe}
          />
        </section>
      )}
    </>
  );
}

const collator = new Intl.Collator(LOCALE, { sensitivity: 'base' });

/** By the name the members see, in the app's language (as the members' list sorts, D25). */
function byName(a: Attendee, b: Attendee): number {
  return collator.compare(a.displayName, b.displayName) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** Who gave one answer: its heading with the count, then the names, or Nobody yet. */
function Attendees({ heading, people }: { heading: string; people: readonly Attendee[] }) {
  const listId = useId();
  return (
    <>
      <h3 id={listId}>{heading}</h3>
      {people.length === 0 ? (
        <p className="muted">{t('event.nobody')}</p>
      ) : (
        <ul className="names" aria-labelledby={listId}>
          {[...people].sort(byName).map((person) => (
            <li key={person.id}>{person.displayName}</li>
          ))}
        </ul>
      )}
    </>
  );
}
